import { useState, useEffect } from "react"
import { Download, FileText, Image as ImageIcon, X, ZoomIn, ZoomOut, RotateCw } from "lucide-react"
import { Button } from "../ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../Card"

export default function ImageBlock({ block, hideTitle = false, isConcatenated = false }) {
  const { content } = block
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleImageClick = () => {
    setIsModalOpen(true)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleCloseModal()
      }
    }
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isModalOpen])

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5))
  }

  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setDragStart({ 
        x: e.clientX - position.x, 
        y: e.clientY - position.y 
      })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      e.preventDefault()
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoom(prev => Math.max(0.5, Math.min(3, prev + delta)))
  }

  return (
    <div className="space-y-6">
      {/* Image Display */}
      {isConcatenated ? (
        // Rendu fluide sans Card pour les blocs concaténés
        <div className="space-y-4 my-8">
          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden">
            <img
              src={content.imageUrl}
              alt={content.alt}
              className="w-full h-auto max-h-[420px] object-contain mx-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleImageClick}
              onError={(e) => {
                e.target.style.display = 'none'
                e.target.nextSibling.style.display = 'flex'
              }}
            />
            {/* Fallback for broken images */}
            <div className="hidden w-full h-72 bg-gray-200 flex items-center justify-center rounded-lg">
              <div className="text-center text-gray-500">
                <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                <p>Image non disponible</p>
                <p className="text-sm">{content.imageUrl}</p>
              </div>
            </div>
          </div>

          {/* Caption */}
          {content.caption && (
            <p className="text-gray-600 text-sm italic text-center">
              {content.caption}
            </p>
          )}
        </div>
      ) : (
        <Card>
          {!hideTitle && (
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {block.title}
              </CardTitle>
              {block.description && (
                <p className="text-gray-600">{block.description}</p>
              )}
            </CardHeader>
          )}
          <CardContent className={hideTitle ? 'pt-6' : ''}>
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={content.imageUrl}
                  alt={content.alt}
                  className="w-full h-auto max-h-96 object-contain mx-auto cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={handleImageClick}
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                {/* Fallback for broken images */}
                <div className="hidden w-full h-64 bg-gray-200 flex items-center justify-center rounded-lg">
                  <div className="text-center text-gray-500">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                    <p>Image non disponible</p>
                    <p className="text-sm">{content.imageUrl}</p>
                  </div>
                </div>
              </div>

              {/* Caption */}
              {content.caption && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <span className="font-medium">Description : </span>
                    {content.caption}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Image Preview Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={handleCloseModal}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          {/* Close Button */}
          <button
            onClick={handleCloseModal}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Zoom Controls */}
          <div className="absolute top-4 left-4 z-10 flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleZoomIn()
              }}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              aria-label="Zoom avant"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleZoomOut()
              }}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              aria-label="Zoom arrière"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleResetZoom()
              }}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              aria-label="Réinitialiser le zoom"
            >
              <RotateCw className="w-5 h-5" />
            </button>
          </div>

          {/* Image Container */}
          <div
            className="relative max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              onMouseDown={handleMouseDown}
              style={{
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none'
              }}
            >
              <img
                src={content.imageUrl}
                alt={content.alt}
                className="max-w-full max-h-[90vh] object-contain select-none"
                draggable={false}
                style={{
                  transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                }}
              />
            </div>
          </div>

          {/* Zoom Indicator */}
          {zoom !== 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
              {Math.round(zoom * 100)}%
            </div>
          )}
        </div>
      )}

      {/* Resources */}
      {content.resources && content.resources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Ressources associées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {content.resources.map((resource, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">Ressource {index + 1}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">{resource}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="inline-flex items-center justify-center gap-2 h-10 leading-none"
                    asChild
                  >
                    <a
                      href={resource}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 h-10 leading-none"
                    >
                      <Download className="w-4 h-4" />
                      <span>Télécharger</span>
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
